import { Video, X } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { RENDEZ_VOUS_KIND_LABELS, type RendezVousKind } from '@jeraisonne/db';
import { isVisioRoomJoinable } from '@jeraisonne/db/visio';
import { useAuth } from '@/lib/auth';
import { useNow } from '@/hooks/use-now';
import { useJoinVisioRoom } from '@/lib/visio/handoff';
import { useArchiveRendezVous } from '@/lib/rendezvous-archive';

export function formatRendezVousDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** Forme générique côté élève (`otherPartyName` = son tuteur) ET côté tuteur (`otherPartyName` = son élève). */
export interface UpcomingRendezVousItem {
  reservationId: string;
  otherPartyName: string;
  startsAt: string;
  endsAt: string;
  kind: RendezVousKind;
}

/**
 * Carte "prochain rendez-vous" (cours/étude/express/entretien confirmé) — le
 * bouton "Rejoindre" n'apparaît que dans la fenêtre de rejoignabilité
 * (-10min/+15min, `isVisioRoomJoinable`). Réutilisée sur le tableau de bord
 * (élève ET tuteur) et sur la liste complète `/rendez-vous`.
 *
 * La croix archive le rendez-vous UNIQUEMENT pour l'utilisateur courant
 * (migration 0240) : il disparaît de son tableau de bord mais reste visible
 * pour l'autre participant, et reste consultable dans "Rendez-vous passés".
 */
export function UpcomingRendezVousCard({ item }: { item: UpcomingRendezVousItem }) {
  const { user } = useAuth();
  const now = useNow(30_000);
  const joinVisioRoom = useJoinVisioRoom();
  const archive = useArchiveRendezVous(user?.id);
  const joinable = isVisioRoomJoinable(
    now,
    new Date(item.startsAt).getTime(),
    new Date(item.endsAt).getTime(),
  );

  function handleArchiveClick() {
    const confirmed = window.confirm(
      "Archiver ce rendez-vous ? Il n'apparaîtra plus dans ton tableau de bord, mais tu pourras le retrouver dans tes rendez-vous passés. Les autres participants continueront à le voir normalement.",
    );
    if (!confirmed) return;
    archive.mutate(item.reservationId);
  }

  return (
    <div className="border-ink-100 relative flex flex-col gap-3 rounded-2xl border bg-white p-4 pr-11 sm:flex-row sm:items-center">
      <button
        type="button"
        onClick={handleArchiveClick}
        disabled={archive.isPending}
        aria-label="Archiver ce rendez-vous"
        className="text-ink-300 hover:bg-ink-100 hover:text-ink-600 absolute right-0 top-0 flex h-11 w-11 items-center justify-center rounded-full transition-colors active:scale-95 disabled:opacity-50"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-ink-900 truncate text-sm font-semibold">{item.otherPartyName}</p>
        <p className="text-ink-500 mt-0.5 text-xs first-letter:uppercase">
          {formatRendezVousDate(item.startsAt)}
        </p>
        <span className="bg-ink-100 text-ink-600 mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold">
          {RENDEZ_VOUS_KIND_LABELS[item.kind]}
        </span>
      </div>
      {joinable && (
        <Button
          onClick={() => joinVisioRoom.mutate(item.reservationId)}
          loading={joinVisioRoom.isPending}
          size="sm"
          className="min-h-[2.75rem] w-full active:scale-[0.98] sm:w-auto"
        >
          <Video className="size-4" />
          Rejoindre la salle
        </Button>
      )}
    </div>
  );
}
